/**
 * Обзор проекта — Dashboard.
 *
 * Отвечает на один вопрос: что представляет собой проект прямо сейчас и чего
 * ему не хватает. Каждый блок показывает краткое состояние и ведёт к исходным
 * данным — Dashboard не заменяет списки, а объясняет, куда смотреть
 * (документ «Итоговое описание», §20).
 *
 * Загружается одним запросом.
 */

import {
  ActivityIcon,
  AlertOctagonIcon,
  ArrowRightIcon,
  DownloadIcon,
  UploadIcon,
  FileJsonIcon,
  FileTextIcon,
  CalendarClockIcon,
  GanttChartIcon,
  CircleHelpIcon,
  ClipboardListIcon,
  FlagIcon,
  GavelIcon,
  MicroscopeIcon,
  PackageIcon,
  TargetIcon,
  TriangleAlertIcon,
  UsersIcon,
  WalletIcon,
  ZapIcon,
  CheckCircle2Icon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type {
  ActivityDayDto,
  ActivityDto,
  DashboardDto,
  DeadlineDto,
  ItemSummaryDto,
  ProjectDto,
  StageRequirementDto,
} from '@project-os/contracts';
import {
  formatMoney,
  isOverdue,
  PROJECT_STATUS_LABELS_RU,
  REQUIREMENT_STATUS_LABELS_RU,
  WORKSTREAM_STATUS_LABELS_RU,
} from '@project-os/contracts';
import { useDashboard } from '@/api/stages';
import { useProjectMembers } from '@/api/members';
import { useWorkstreams } from '@/api/workstreams';
import { ItemDetailPanel } from '../items/ItemDetailPanel';
import { ScheduleCard } from '../timeline/ScheduleCard';
import { ImportProjectDialog } from '../projects/ImportProjectDialog';
import { Progress } from '@/components/common/progress';
import { ProgressRing } from '@/components/common/progress-ring';
import { ItemStatusBadge, StatusBadge } from '@/components/common/status-badge';
import { CardListSkeleton, ErrorState } from '@/components/common/states';
import { exportUrl } from '@/api/search';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { UserAvatar } from '@/components/common/user-avatar';
import { daysSince, formatDate, formatDayRelative, formatRelative, pluralRu } from '@/lib/format';
import { cn } from '@/lib/utils';
import { memberColorStyle } from '@/lib/member-color';
import { ItemTypeIcon } from '../items/item-icon';
import { useProjectContext } from './use-project-context';

const PROJECT_STATUS_TONES = {
  IDEA: 'neutral',
  ACTIVE: 'success',
  PAUSED: 'warning',
  COMPLETED: 'neutral',
} as const;

const WORKSTREAM_TONES = {
  NOT_STARTED: 'neutral',
  ACTIVE: 'success',
  BLOCKED: 'danger',
  DONE: 'neutral',
} as const;

export function OverviewPage() {
  const project = useProjectContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isPending, isError, error, refetch } = useDashboard(project.id);
  const { data: workstreams = [] } = useWorkstreams(project.id);
  const { data: members = [] } = useProjectMembers(project.id);

  const itemsPath = `/projects/${project.id}/items`;
  const canEdit = project.permissions.includes('ITEM_WRITE');

  // Карточка открывается прямо на обзоре: переход на другую страницу ради
  // одного элемента теряет контекст, ради которого обзор и открывали.
  const selectedId = searchParams.get('item');
  const setItem = (itemId: string | null) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (itemId) next.set('item', itemId);
        else next.delete('item');
        return next;
      },
      { replace: true },
    );

  return (
    <>
      <PageHeader
        title={project.name}
        description={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <StatusBadge
              label={PROJECT_STATUS_LABELS_RU[project.status]}
              tone={PROJECT_STATUS_TONES[project.status]}
            />
            <span className="text-xs">
              Владелец: <span className="text-foreground">{project.owner.name}</span>
            </span>
            {project.targetDate ? (
              <span className="text-xs">Целевая дата: {formatDate(project.targetDate)}</span>
            ) : null}
            <span className="text-xs">
              Обновлён {formatRelative(project.lastActivityAt ?? project.updatedAt)}
            </span>
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <UploadChangesButton project={project} />
            <DownloadProjectButton projectId={project.id} />
          </div>
        }
      />

      <PageBody className="space-y-4">
        {isPending ? (
          <CardListSkeleton count={4} />
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <>
            <NowCard
              nextActions={data.nextActions}
              deadlines={data.deadlines ?? []}
              blockers={data.blockers}
              onOpenItem={setItem}
              itemsPath={itemsPath}
            />

            {/* Расписание: обзор отвечает на вопрос «где мы», а он в первую
                очередь про сроки. */}
            {data.timeline ? (
              <Card className="fade-in">
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <GanttChartIcon className="size-4 text-muted-foreground" />
                    Расписание
                  </CardTitle>
                  <Link
                    to={`/projects/${project.id}/stages`}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    этапы
                  </Link>
                </CardHeader>
                <CardContent>
                  <ScheduleCard
                    projectId={project.id}
                    timeline={data.timeline}
                    members={members.map((member) => member.user)}
                    canEdit={canEdit}
                    onOpenItem={setItem}
                    onOpenStage={() => navigate(`/projects/${project.id}/stages`)}
                  />
                </CardContent>
              </Card>
            ) : null}

            {/* Цель и готовность этапа — главный ответ на вопрос «где мы».
                Счётчики стоят под целью, в той же колонке: карточка цели
                занимает две строки текста, и без них рядом с высокой
                карточкой этапа оставалась бы четверть экрана пустоты. */}
            <div className="grid items-start gap-4 lg:grid-cols-3">
              <div className="space-y-4 lg:col-span-2">
                <Card className="fade-in">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <TargetIcon className="size-4 text-muted-foreground" />
                      Текущая цель
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {data.project.currentGoal ? (
                      <p className="text-sm leading-relaxed">{data.project.currentGoal}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Цель не задана. Её можно указать в настройках проекта.
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Счётчики ведут в отфильтрованные списки */}
                <div className="fade-in-stagger grid gap-3 sm:grid-cols-2">
                  <MetricTile
                    icon={<ClipboardListIcon />}
                    label="Следующие действия"
                    value={data.nextActions.length}
                    to={`${itemsPath}?view=next-actions`}
                  />
                  <MetricTile
                    icon={<AlertOctagonIcon />}
                    label="Открытые блокеры"
                    value={data.blockers.length}
                    tone="danger"
                    to={`${itemsPath}?type=BLOCKER`}
                  />
                  <MetricTile
                    icon={<CircleHelpIcon />}
                    label="Открытые вопросы"
                    value={data.openQuestions.length}
                    to={`${itemsPath}?view=open-questions`}
                  />
                  <MetricTile
                    icon={<UsersIcon />}
                    label="Участники"
                    value={data.project.counters.members}
                    to={`/projects/${project.id}/members`}
                  />
                </div>
              </div>

              <StageReadinessCard
                projectId={project.id}
                stage={data.currentStage}
                missing={data.missingRequirements}
              />
            </div>

            <div className="grid items-start gap-4 lg:grid-cols-3">
              <DeadlinesCard
                deadlines={data.deadlines ?? []}
                projectId={project.id}
                onOpenItem={setItem}
              />
              <ActivityTrendCard
                trend={data.activityTrend ?? []}
                changes={data.recentChanges ?? []}
              />
            </div>

            {/* Что мешает и что делать дальше */}
            <div className="grid gap-4 lg:grid-cols-2">
              <ItemsCard
                title="Следующие действия"
                icon={<ClipboardListIcon className="size-4" />}
                items={data.nextActions}
                projectId={project.id}
                emptyText="Ни один элемент не отмечен как следующий шаг. Отметьте задачу, исследование, вопрос или блокер."
                to={`${itemsPath}?view=next-actions`}
                showDue
              />

              <BlockersCard
                blockers={data.blockers}
                risks={data.criticalRisks}
                projectId={project.id}
                itemsPath={itemsPath}
              />

              <ItemsCard
                title="Открытые вопросы"
                icon={<CircleHelpIcon className="size-4" />}
                items={data.openQuestions}
                projectId={project.id}
                emptyText="Открытых вопросов нет."
                to={`${itemsPath}?view=open-questions`}
              />

              <ItemsCard
                title="Действующие решения"
                icon={<GavelIcon className="size-4" />}
                items={data.recentDecisions}
                projectId={project.id}
                emptyText="Решения не зафиксированы. Если решение принято в переписке, запишите его здесь."
                to={`${itemsPath}?view=decisions`}
              />
            </div>

            {/* Что накоплено */}
            <div className="grid gap-4 lg:grid-cols-2">
              <KnowledgeCard knowledge={data.knowledge} itemsPath={itemsPath} />
              <AssetsCard assets={data.assets} projectId={project.id} itemsPath={itemsPath} />
            </div>

            <ResourceSignalsCard signals={data.resourceSignals} projectId={project.id} />

            <WorkstreamsCard workstreams={data.workstreams} itemsPath={itemsPath} />

            <RecentActivityCard activity={data.recentActivity} projectId={project.id} />
          </>
        )}
      </PageBody>

      <ItemDetailPanel
        projectId={project.id}
        itemId={selectedId}
        onClose={() => setItem(null)}
        onOpenItem={setItem}
        canEdit={canEdit}
        canReview={project.permissions.includes('REVIEW_WRITE')}
        workstreams={workstreams}
        members={members.map((member) => member.user)}
      />
    </>
  );
}

// --- Ближайшие дедлайны --------------------------------------------------

/**
 * Что горит: элементы со сроком и этапы с целевой датой в одном списке.
 *
 * Разделять их значило бы заставлять человека складывать два списка в голове.
 * Просроченное идёт первым и подписано красным — оно требует решения сейчас,
 * а не через неделю.
 */
function DeadlinesCard({
  deadlines,
  projectId,
  onOpenItem,
}: {
  deadlines: DeadlineDto[];
  projectId: string;
  onOpenItem: (itemId: string) => void;
}) {
  const overdue = deadlines.filter((entry) => entry.daysLeft < 0).length;

  return (
    <Card className="fade-in lg:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <CalendarClockIcon
            className={cn('size-4', overdue > 0 ? 'text-tone-danger' : 'text-muted-foreground')}
          />
          Ближайшие сроки
          {overdue > 0 ? (
            <span className="text-2xs font-normal text-tone-danger">
              {pluralRu(overdue, ['просрочен', 'просрочено', 'просрочено'])}
            </span>
          ) : null}
        </CardTitle>
        <Link
          to={`/projects/${projectId}/items?mode=calendar`}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          календарь
        </Link>
      </CardHeader>
      <CardContent>
        {deadlines.length === 0 ? (
          <p className="text-xs leading-relaxed text-muted-foreground">
            На ближайший месяц сроков нет. Срок ставится в карточке элемента или перетаскиванием в
            календаре.
          </p>
        ) : (
          <ul className="space-y-0.5">
            {deadlines.map((entry) => {
              const late = entry.daysLeft < 0;
              const soon = entry.daysLeft >= 0 && entry.daysLeft <= 3;

              const row = (
                <span className="flex items-center gap-2">
                  {entry.kind === 'stage' ? (
                    <FlagIcon className="size-3.5 shrink-0 text-muted-foreground" />
                  ) : (
                    <ItemTypeIcon type={entry.itemType!} className="size-3.5 shrink-0" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm">{entry.title}</span>
                  <span
                    className={cn(
                      'shrink-0 text-2xs tabular-nums',
                      late
                        ? 'text-tone-danger'
                        : soon
                          ? 'text-tone-warning'
                          : 'text-muted-foreground',
                    )}
                  >
                    {formatDayRelative(entry.date)}
                  </span>
                </span>
              );

              return (
                <li key={`${entry.kind}-${entry.id}`}>
                  {entry.kind === 'item' ? (
                    <button
                      type="button"
                      onClick={() => onOpenItem(entry.id)}
                      className="row-hover -mx-2 block w-full rounded-md px-2 py-1.5 text-left"
                    >
                      {row}
                    </button>
                  ) : (
                    <Link
                      to={`/projects/${projectId}/stages`}
                      className="row-hover -mx-2 block rounded-md px-2 py-1.5"
                    >
                      {row}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// --- Активность за две недели --------------------------------------------

/**
 * Мини-график активности и три последних изменения.
 *
 * График отвечает на вопрос «проект жив?»: ровная пустая линия за две недели
 * говорит больше, чем любой отчёт. Дни без событий рисуются нулями, а не
 * пропускаются — иначе пауза выглядела бы как непрерывная работа.
 */
function ActivityTrendCard({
  trend,
  changes,
}: {
  trend: ActivityDayDto[];
  changes: ActivityDto[];
}) {
  const max = Math.max(1, ...trend.map((day) => day.count));
  const total = trend.reduce((sum, day) => sum + day.count, 0);
  const today = trend.at(-1)?.date;

  return (
    <Card className="fade-in">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <ActivityIcon className="size-4 text-muted-foreground" />
          Активность за две недели
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div
            className="flex h-12 items-end gap-[3px]"
            role="img"
            aria-label={`Действий за две недели: ${total}`}
          >
            {trend.map((day) => (
              <Tooltip key={day.date}>
                <TooltipTrigger asChild>
                  <div className="flex h-full flex-1 items-end">
                    <div
                      className={cn(
                        'w-full rounded-sm transition-colors',
                        day.count === 0
                          ? 'bg-muted'
                          : day.date === today
                            ? 'bg-primary'
                            : 'bg-primary/45 hover:bg-primary/70',
                      )}
                      // Минимальная высота у нулевого дня — 2 px: полоса
                      // нулевой высоты выглядела бы как отсутствие данных.
                      style={{ height: `${Math.max((day.count / max) * 100, 4)}%` }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-2xs">
                    {formatDate(day.date)} —{' '}
                    {pluralRu(day.count, ['действие', 'действия', 'действий'])}
                  </p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
          <p className="mt-1.5 text-2xs text-muted-foreground">
            Всего {pluralRu(total, ['действие', 'действия', 'действий'])}
            {total === 0 ? ' — в проекте две недели ничего не происходило' : ''}
          </p>
        </div>

        {changes.length > 0 ? (
          <div className="border-t border-border/40 pt-2.5">
            <p className="mb-1 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
              Последние изменения
            </p>
            <ul className="space-y-1">
              {changes.map((entry) => (
                <li key={entry.id} className="flex items-baseline gap-1.5 text-2xs">
                  <span className="min-w-0 flex-1 truncate">
                    <span className="text-foreground">{entry.user?.name ?? 'Система'}</span>
                    <span className="text-muted-foreground"> · {entry.labelRu}</span>
                    {entry.entityTitle ? (
                      <span className="text-muted-foreground"> · {entry.entityTitle}</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-muted-foreground/80">
                    {formatRelative(entry.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// --- Готовность этапа ----------------------------------------------------

function StageReadinessCard({
  projectId,
  stage,
  missing,
}: {
  projectId: string;
  stage: {
    id: string;
    name: string;
    targetDate: string | null;
    readiness: { done: number; total: number; skipped: number; blocked: number };
  } | null;
  missing: StageRequirementDto[];
}) {
  if (!stage) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Готовность этапа</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Текущий этап не задан. Пока нет измеримых требований, прогресс проекта нечем измерить.
          </p>
          <Link
            to={`/projects/${projectId}/stages`}
            className="icon-nudge mt-3 inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Создать этап
            <ArrowRightIcon className="size-3" />
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <FlagIcon className="size-4 text-muted-foreground" />
          {stage.name}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <ProgressRing value={stage.readiness.done} max={stage.readiness.total} size={52} />
          <div className="min-w-0 flex-1">
            <p className="text-sm tabular-nums">
              <span className="font-semibold">{stage.readiness.done}</span> из{' '}
              <span className="font-semibold">{stage.readiness.total}</span> требований выполнено
            </p>
            <Progress
              className="mt-1.5"
              value={stage.readiness.done}
              max={stage.readiness.total}
              label={`Выполнено ${stage.readiness.done} из ${stage.readiness.total}`}
            />
            {stage.readiness.skipped > 0 || stage.readiness.blocked > 0 ? (
              <p className="mt-1.5 text-2xs text-muted-foreground">
                {stage.readiness.blocked > 0 ? `заблокировано ${stage.readiness.blocked}` : null}
                {stage.readiness.blocked > 0 && stage.readiness.skipped > 0 ? ' · ' : null}
                {stage.readiness.skipped > 0 ? `пропущено ${stage.readiness.skipped}` : null}
              </p>
            ) : null}
          </div>
        </div>

        {missing.length > 0 ? (
          <>
            <p className="mt-3 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
              Не хватает
            </p>
            <ul className="mt-1.5 space-y-1">
              {missing.slice(0, 5).map((requirement) => (
                <li key={requirement.id} className="flex items-start gap-1.5 text-xs">
                  <span
                    aria-hidden
                    className={cn(
                      'mt-0.5',
                      requirement.status === 'BLOCKED'
                        ? 'text-tone-danger'
                        : 'text-muted-foreground',
                    )}
                  >
                    {requirement.status === 'BLOCKED' ? '!' : '○'}
                  </span>
                  <span className="min-w-0 flex-1">
                    {requirement.title}
                    {requirement.status === 'BLOCKED' ? (
                      <span className="ml-1.5 text-tone-danger">
                        {REQUIREMENT_STATUS_LABELS_RU.BLOCKED.toLowerCase()}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="mt-3 text-xs text-tone-success">Все требования этапа выполнены.</p>
        )}

        {stage.targetDate ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Целевая дата этапа: {formatDate(stage.targetDate)}
          </p>
        ) : null}

        <Link
          to={`/projects/${projectId}/stages`}
          className="icon-nudge mt-3 inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          Открыть этап
          <ArrowRightIcon className="size-3" />
        </Link>
      </CardContent>
    </Card>
  );
}

// --- Плитка счётчика -----------------------------------------------------

function MetricTile({
  icon,
  label,
  value,
  tone,
  to,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  tone?: 'danger';
  to: string;
}) {
  return (
    <Link
      to={to}
      className="surface-card surface-card-interactive flex items-center gap-3 border border-border/50 p-3.5"
    >
      <span
        className={cn(
          '[&>svg]:size-4.5',
          tone === 'danger' && value > 0 ? 'text-tone-danger' : 'text-muted-foreground',
        )}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-lg leading-none font-semibold tabular-nums">{value}</span>
        <span className="mt-1 block truncate text-xs text-muted-foreground">{label}</span>
      </span>
    </Link>
  );
}

// --- Список элементов ----------------------------------------------------

function ItemLink({
  item,
  projectId,
  showDue,
}: {
  item: ItemSummaryDto;
  projectId: string;
  showDue?: boolean;
}) {
  return (
    <li>
      <Link
        to={`/projects/${projectId}/items?item=${item.id}`}
        className="-mx-2 flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-accent"
      >
        <ItemTypeIcon type={item.type} className="mt-0.5 size-3.5" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm">{item.title}</span>
          <span className="mt-0.5 flex flex-wrap gap-x-2 text-2xs text-muted-foreground">
            {item.owner ? <span>{item.owner.name}</span> : <span>без ответственного</span>}
            {showDue && item.dueDate ? <span>срок {formatDate(item.dueDate)}</span> : null}
            {item.workstream ? <span>{item.workstream.name}</span> : null}
          </span>
        </span>
        <ItemStatusBadge type={item.type} status={item.status} />
      </Link>
    </li>
  );
}

function ItemsCard({
  title,
  icon,
  items,
  projectId,
  emptyText,
  to,
  showDue,
}: {
  title: string;
  icon: ReactNode;
  items: ItemSummaryDto[];
  projectId: string;
  emptyText: string;
  to: string;
  showDue?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{icon}</span>
          {title}
        </CardTitle>
        {items.length > 0 ? (
          <Link to={to} className="text-xs text-muted-foreground hover:text-foreground">
            все
          </Link>
        ) : null}
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-xs leading-relaxed text-muted-foreground">{emptyText}</p>
        ) : (
          <ul className="space-y-0.5">
            {items.map((item) => (
              <ItemLink key={item.id} item={item} projectId={projectId} showDue={showDue} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Блокеры и критические риски — то, что мешает прямо сейчас. */
function BlockersCard({
  blockers,
  risks,
  projectId,
  itemsPath,
}: {
  blockers: ItemSummaryDto[];
  risks: ItemSummaryDto[];
  projectId: string;
  itemsPath: string;
}) {
  const empty = blockers.length === 0 && risks.length === 0;

  return (
    <Card className={cn(blockers.length > 0 && 'border-tone-danger-border')}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <AlertOctagonIcon
            className={cn(
              'size-4',
              blockers.length > 0 ? 'text-tone-danger' : 'text-muted-foreground',
            )}
          />
          Блокеры и критические риски
        </CardTitle>
        {!empty ? (
          <Link
            to={`${itemsPath}?view=risks-blockers`}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            все
          </Link>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {empty ? (
          <p className="text-xs text-muted-foreground">
            Ничего не блокирует работу, критических рисков нет.
          </p>
        ) : null}

        {blockers.length > 0 ? (
          <ul className="space-y-0.5">
            {blockers.map((item) => {
              const blockedSince = item.preview.blockedSince as string | undefined;
              const age = daysSince(blockedSince ?? item.createdAt);
              return (
                <li key={item.id}>
                  <Link
                    to={`/projects/${projectId}/items?item=${item.id}`}
                    className="-mx-2 flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-accent"
                  >
                    <ItemTypeIcon type={item.type} className="mt-0.5 size-3.5" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{item.title}</span>
                      <span className="mt-0.5 block text-2xs text-muted-foreground">
                        {item.owner?.name ?? 'без ответственного'}
                        {age !== null && age > 0
                          ? ` · держится ${pluralRu(age, ['день', 'дня', 'дней'])}`
                          : null}
                      </span>
                    </span>
                    <ItemStatusBadge type={item.type} status={item.status} />
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : null}

        {risks.length > 0 ? (
          <div>
            <p className="mb-1 flex items-center gap-1.5 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
              <TriangleAlertIcon className="size-3" />
              Критические риски
            </p>
            <ul className="space-y-0.5">
              {risks.map((item) => (
                <ItemLink key={item.id} item={item} projectId={projectId} />
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// --- Знания и активы -----------------------------------------------------

function KnowledgeCard({
  knowledge,
  itemsPath,
}: {
  knowledge: {
    researchApproved: number;
    factsVerified: number;
    metricsAdded: number;
    sourcesAdded: number;
    recent: ItemSummaryDto[];
  };
  itemsPath: string;
}) {
  const tiles = [
    { label: 'исследований принято', value: knowledge.researchApproved, filter: 'type=RESEARCH' },
    { label: 'фактов подтверждено', value: knowledge.factsVerified, filter: 'type=FACT' },
    { label: 'метрик добавлено', value: knowledge.metricsAdded, filter: 'type=METRIC' },
    { label: 'источников добавлено', value: knowledge.sourcesAdded, filter: 'type=SOURCE' },
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <MicroscopeIcon className="size-4 text-muted-foreground" />
          Новые знания за месяц
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2">
          {tiles.map((tile) => (
            <Link
              key={tile.label}
              to={`${itemsPath}?${tile.filter}`}
              className="rounded-md border px-3 py-2 hover:bg-accent"
            >
              <span className="block text-base leading-none font-semibold tabular-nums">
                {tile.value}
              </span>
              <span className="mt-1 block text-2xs text-muted-foreground">{tile.label}</span>
            </Link>
          ))}
        </div>

        {knowledge.recent.length > 0 ? (
          <ul className="mt-3 space-y-0.5">
            {knowledge.recent.slice(0, 4).map((item) => (
              <li key={item.id} className="flex items-center gap-2 text-xs">
                <ItemTypeIcon type={item.type} className="size-3" />
                <span className="truncate">{item.title}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}

function AssetsCard({
  assets,
  projectId,
  itemsPath,
}: {
  assets: {
    approved: number;
    readyForReview: number;
    draft: number;
    outdated: number;
    needsChanges: number;
    recent: ItemSummaryDto[];
  };
  projectId: string;
  itemsPath: string;
}) {
  const tiles = [
    { label: `${assets.approved} принято`, tone: 'success' as const },
    { label: `${assets.readyForReview} на проверке`, tone: 'warning' as const },
    // «Черновик» склоняется по числу, остальные подписи — нет.
    {
      label: pluralRu(assets.draft, ['черновик', 'черновика', 'черновиков']),
      tone: 'neutral' as const,
    },
    { label: `${assets.outdated} устарело`, tone: 'neutral' as const },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <PackageIcon className="size-4 text-muted-foreground" />
          Активы
        </CardTitle>
        <Link
          to={`${itemsPath}?type=ASSET`}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          все
        </Link>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1.5">
          {tiles.map((tile) => (
            <StatusBadge key={tile.label} label={tile.label} tone={tile.tone} />
          ))}
        </div>

        {assets.recent.length > 0 ? (
          <ul className="mt-3 space-y-0.5">
            {assets.recent.map((item) => (
              <ItemLink key={item.id} item={item} projectId={projectId} />
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground">Созданных результатов пока нет.</p>
        )}
      </CardContent>
    </Card>
  );
}

// --- Направления и история ----------------------------------------------

function WorkstreamsCard({
  workstreams,
  itemsPath,
}: {
  workstreams: {
    id: string;
    name: string;
    status: keyof typeof WORKSTREAM_TONES;
    owner: { name: string } | null;
    openTasks: number;
    openQuestions: number;
    openBlockers: number;
    lastActivityAt: string | null;
  }[];
  itemsPath: string;
}) {
  if (workstreams.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Направления</CardTitle>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Процент готовности не рассчитывается: без явных требований такое число было бы выдуманным
        </p>
      </CardHeader>
      <CardContent>
        <ul className="divide-y rounded-md border">
          {workstreams.map((workstream) => (
            <li
              key={workstream.id}
              className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2"
            >
              <Link
                to={`${itemsPath}?workstreamId=${workstream.id}`}
                className="min-w-40 flex-1 text-sm hover:underline"
              >
                {workstream.name}
              </Link>
              <StatusBadge
                label={WORKSTREAM_STATUS_LABELS_RU[workstream.status]}
                tone={WORKSTREAM_TONES[workstream.status]}
              />
              <span className="w-40 truncate text-xs text-muted-foreground">
                {workstream.owner?.name ?? 'без ответственного'}
              </span>
              <span className="flex gap-3 text-xs text-muted-foreground tabular-nums">
                <span title="Открытые задачи">{workstream.openTasks} зад.</span>
                <span title="Открытые вопросы">{workstream.openQuestions} вопр.</span>
                <span
                  title="Открытые блокеры"
                  className={workstream.openBlockers > 0 ? 'text-tone-danger' : undefined}
                >
                  {workstream.openBlockers} блок.
                </span>
              </span>
              <span className="w-28 text-right text-2xs text-muted-foreground">
                {formatRelative(workstream.lastActivityAt)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function RecentActivityCard({
  activity,
  projectId,
}: {
  activity: {
    id: string;
    labelRu: string;
    user: { name: string } | null;
    entityTitle: string | null;
    entityId: string | null;
    entityType: string;
    createdAt: string;
  }[];
  projectId: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="text-sm">Последние действия</CardTitle>
        <Link
          to={`/projects/${projectId}/activity`}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          вся история
        </Link>
      </CardHeader>
      <CardContent>
        {activity.length === 0 ? (
          <p className="text-xs text-muted-foreground">Действий пока нет.</p>
        ) : (
          <ul className="space-y-1.5">
            {activity.map((entry) => (
              <li key={entry.id} className="flex items-start gap-2 text-xs">
                <UserAvatar user={entry.user} size="sm" className="mt-0.5" />
                <span className="min-w-0 flex-1">
                  <span className="text-foreground">{entry.user?.name ?? 'Система'}</span>
                  <span className="text-muted-foreground"> · {entry.labelRu}</span>
                  {entry.entityTitle ? (
                    entry.entityType === 'ProjectItem' && entry.entityId ? (
                      <Link
                        to={`/projects/${projectId}/items?item=${entry.entityId}`}
                        className="ml-1 hover:underline"
                      >
                        {entry.entityTitle}
                      </Link>
                    ) : (
                      <span className="ml-1">{entry.entityTitle}</span>
                    )
                  ) : null}
                </span>
                <span className="shrink-0 text-2xs text-muted-foreground">
                  {formatRelative(entry.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// --- Сигналы по ресурсам и финансам --------------------------------------

/**
 * Организационные сигналы: не ошибки, а факты, которые стоит заметить.
 * Аккаунт без ответственного некому будет восстановить, пробный период
 * закончится, просроченный платёж отключит сервис.
 */
function ResourceSignalsCard({
  signals,
  projectId,
}: {
  signals: DashboardDto['resourceSignals'] | undefined;
  projectId: string;
}) {
  if (!signals) return null;

  const groups = [
    {
      key: 'accounts',
      label: 'Аккаунты без ответственного',
      items: signals.accountsWithoutOwner,
      to: `/projects/${projectId}/resources?tab=accounts`,
      tone: 'warning' as const,
      explain: 'Если человек уйдёт, восстанавливать доступ будет некому.',
    },
    {
      key: 'trial',
      label: 'Инструменты в пробном периоде',
      items: signals.toolsOnTrial,
      to: `/projects/${projectId}/resources?tab=tools`,
      tone: 'neutral' as const,
      explain: 'Пробный период закончится — решение о продлении нужно принять заранее.',
    },
    {
      key: 'overdue',
      label: 'Просроченные платежи',
      items: signals.overdueExpenses,
      to: `/projects/${projectId}/finance`,
      tone: 'danger' as const,
      explain: 'Неоплаченный счёт отключит сервис.',
    },
  ].filter((group) => group.items.length > 0);

  const nothing = groups.length === 0 && signals.recurringMonthly.length === 0;
  if (nothing) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <WalletIcon className="size-4 text-muted-foreground" />
          Ресурсы и расходы
        </CardTitle>
        <Link
          to={`/projects/${projectId}/finance`}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          финансы
        </Link>
      </CardHeader>
      <CardContent className="space-y-3">
        {signals.recurringMonthly.length > 0 ? (
          <div>
            <p className="mb-1 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
              Периодические расходы в месяц
            </p>
            <div className="flex flex-wrap gap-3">
              {signals.recurringMonthly.map((total) => (
                <span key={total.currency} className="text-sm font-semibold tabular-nums">
                  {formatMoney(total)}
                  <span className="ml-1 text-2xs font-normal text-muted-foreground">
                    {pluralRu(total.count, ['расход', 'расхода', 'расходов'])}
                  </span>
                </span>
              ))}
            </div>
            <p className="mt-1 text-2xs text-muted-foreground/80">
              Разные валюты не складываются: курса в системе нет.
            </p>
          </div>
        ) : null}

        {groups.map((group) => (
          <div key={group.key}>
            <p
              className={cn(
                'mb-1 flex items-center gap-1.5 text-2xs font-semibold tracking-wide uppercase',
                group.tone === 'danger'
                  ? 'text-tone-danger'
                  : group.tone === 'warning'
                    ? 'text-tone-warning'
                    : 'text-muted-foreground',
              )}
            >
              {group.tone !== 'neutral' ? <TriangleAlertIcon className="size-3" /> : null}
              {group.label}
            </p>
            <ul className="space-y-0.5">
              {group.items.slice(0, 5).map((item) => (
                <li key={item.id} className="flex items-center gap-2 text-xs">
                  <ItemTypeIcon type={item.type} className="size-3" />
                  <span className="min-w-0 flex-1 truncate">{item.title}</span>
                </li>
              ))}
            </ul>
            <p className="mt-1 text-2xs leading-relaxed text-muted-foreground/80">
              {group.explain}{' '}
              <Link to={group.to} className="hover:underline">
                Открыть раздел
              </Link>
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// --- Сейчас важно --------------------------------------------------------

/**
 * Три задачи, два срока, один блокер.
 *
 * Смысл блока — не полнота, а короткий ответ на вопрос «за что взяться
 * сейчас». Списки ниже на этой же странице показывают всё; здесь намеренно
 * мало строк, иначе блок превращается в ещё один список и перестаёт
 * отвечать на свой вопрос.
 *
 * Числа выбраны так, чтобы блок читался одним взглядом и умещался в строку
 * на широком экране.
 */
function NowCard({
  nextActions,
  deadlines,
  blockers,
  onOpenItem,
  itemsPath,
}: {
  nextActions: ItemSummaryDto[];
  deadlines: DeadlineDto[];
  blockers: ItemSummaryDto[];
  onOpenItem: (itemId: string) => void;
  itemsPath: string;
}) {
  // Срочные задачи: сначала отмеченные следующим действием, среди них —
  // с более ранним сроком.
  const urgent = [...nextActions]
    .sort((left, right) => (left.dueDate ?? '9999').localeCompare(right.dueDate ?? '9999'))
    .slice(0, 3);

  // Сроки, которых нет среди уже показанных задач: повторять одно и то же
  // в соседних колонках незачем.
  const shownIds = new Set(urgent.map((item) => item.id));
  const soon = deadlines.filter((entry) => !shownIds.has(entry.id)).slice(0, 2);

  const blocker = blockers[0] ?? null;

  if (urgent.length === 0 && soon.length === 0 && !blocker) {
    return (
      <Card className="fade-in">
        <CardContent className="flex items-center gap-2.5 py-1">
          <CheckCircle2Icon className="size-4 shrink-0 text-tone-success" />
          <p className="text-sm">Ничего не горит: нет ни срочных задач, ни блокеров.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="fade-in border-primary/30">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <ZapIcon className="size-4 text-primary" />
          Сейчас важно
        </CardTitle>
        <Link
          to={`${itemsPath}?view=next-actions`}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          все задачи
        </Link>
      </CardHeader>

      <CardContent>
        <div className="grid gap-x-6 gap-y-3 lg:grid-cols-3">
          <NowColumn
            heading="Взяться за это"
            empty="Ни одна задача не отмечена следующим действием."
          >
            {urgent.map((item) => (
              <NowRow
                key={item.id}
                title={item.title}
                type={item.type}
                owner={item.owner}
                note={item.dueDate ? formatDayRelative(item.dueDate) : null}
                tone={isOverdue(item.dueDate) ? 'danger' : 'neutral'}
                onClick={() => onOpenItem(item.id)}
              />
            ))}
          </NowColumn>

          <NowColumn
            heading="Ближайшие сроки"
            empty={
              deadlines.length > 0
                ? 'Все ближайшие сроки уже в списке слева.'
                : 'На ближайший месяц сроков нет.'
            }
          >
            {soon.map((entry) => (
              <NowRow
                key={`${entry.kind}-${entry.id}`}
                title={entry.title}
                type={entry.itemType}
                owner={entry.owner}
                note={formatDayRelative(entry.date)}
                tone={entry.daysLeft < 0 ? 'danger' : entry.daysLeft <= 3 ? 'warning' : 'neutral'}
                onClick={() => entry.kind === 'item' && onOpenItem(entry.id)}
              />
            ))}
          </NowColumn>

          <NowColumn heading="Что мешает" empty="Ничего не блокирует работу.">
            {blocker ? (
              <NowRow
                title={blocker.title}
                type={blocker.type}
                owner={blocker.owner}
                note={
                  daysSince(blocker.createdAt) !== null && daysSince(blocker.createdAt)! > 0
                    ? `держится ${pluralRu(daysSince(blocker.createdAt)!, ['день', 'дня', 'дней'])}`
                    : null
                }
                tone="danger"
                onClick={() => onOpenItem(blocker.id)}
              />
            ) : null}
          </NowColumn>
        </div>
      </CardContent>
    </Card>
  );
}

function NowColumn({
  heading,
  empty,
  children,
}: {
  heading: string;
  empty: string;
  children: ReactNode;
}) {
  const isEmpty = !Array.isArray(children) ? !children : children.filter(Boolean).length === 0;

  return (
    <div>
      <p className="mb-1 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
        {heading}
      </p>
      {isEmpty ? (
        <p className="text-xs leading-relaxed text-muted-foreground/80">{empty}</p>
      ) : (
        <ul className="space-y-0.5">{children}</ul>
      )}
    </div>
  );
}

function NowRow({
  title,
  type,
  owner,
  note,
  tone,
  onClick,
}: {
  title: string;
  type: ItemSummaryDto['type'] | null;
  owner: { id: string; name: string } | null;
  note: string | null;
  tone: 'danger' | 'warning' | 'neutral';
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="row-hover -mx-2 flex w-full items-center gap-2 rounded-md px-2 py-1 text-left"
      >
        {type ? (
          <ItemTypeIcon type={type} className="size-3.5 shrink-0" />
        ) : (
          <FlagIcon className="size-3.5 shrink-0 text-muted-foreground" />
        )}

        <span className="min-w-0 flex-1 truncate text-sm">{title}</span>

        {owner ? (
          <span
            className="member-color size-2 shrink-0 rounded-full"
            style={memberColorStyle(owner.id)}
            title={owner.name}
            aria-hidden
          />
        ) : null}

        {note ? (
          <span
            className={cn(
              'shrink-0 text-2xs tabular-nums',
              tone === 'danger'
                ? 'text-tone-danger'
                : tone === 'warning'
                  ? 'text-tone-warning'
                  : 'text-muted-foreground',
            )}
          >
            {note}
          </span>
        ) : null}
      </button>
    </li>
  );
}

// --- Возврат изменённого файла -------------------------------------------

/**
 * Загрузка файла обратно в этот же проект.
 *
 * Обратная половина «Скачать проект», и стоит она рядом с ним не случайно:
 * это одно действие в две стороны. Выгрузить проект одним файлом, поправить
 * его снаружи — руками, редактором, чем угодно, — и вернуть правку сюда.
 *
 * Без кнопки здесь такой оборот приходилось начинать со списка проектов и
 * там же выбирать из выпадающего списка тот проект, в котором человек только
 * что стоял. Изнутри проекта получатель известен и не спрашивается.
 *
 * Видна владельцу и администратору проекта: загрузка файла меняет проект
 * целиком, и это ближе к управлению им, чем к обычной работе внутри.
 */
function UploadChangesButton({ project }: { project: ProjectDto }) {
  const [open, setOpen] = useState(false);

  if (project.role !== 'OWNER' && project.role !== 'ADMIN') return null;

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <UploadIcon />
        Загрузить изменения
      </Button>

      <ImportProjectDialog
        open={open}
        onOpenChange={setOpen}
        workspaces={[]}
        project={{ id: project.id, name: project.name, workspaceId: project.workspaceId }}
      />
    </>
  );
}

// --- Скачивание проекта --------------------------------------------------

/**
 * Выгрузка проекта одним файлом.
 *
 * Стоит на обзоре, а не только в настройках: забрать свои данные — не
 * настройка, а обычное действие, и искать его в разделе конфигурации никто
 * не станет.
 *
 * Доступно любому участнику, включая наблюдателя: файл содержит ровно то,
 * что человек и так видит на экране. Запрет на скачивание не защитил бы
 * ничего — он лишь заставил бы копировать то же самое руками.
 *
 * Скачивание идёт обычной ссылкой, а не запросом: браузер сам сохранит файл
 * с именем из заголовка, и выгрузка не окажется в памяти вкладки.
 */
function DownloadProjectButton({ projectId }: { projectId: string }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline">
          <DownloadIcon />
          Скачать проект
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuItem asChild>
          <a href={exportUrl(projectId, 'json')} download>
            <FileJsonIcon />
            <span className="flex-1">
              JSON
              <span className="block text-2xs text-muted-foreground">
                Проект целиком: читается и человеком, и программой
              </span>
            </span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={exportUrl(projectId, 'markdown')} download>
            <FileTextIcon />
            <span className="flex-1">
              Markdown
              <span className="block text-2xs text-muted-foreground">
                Тот же проект текстом, для чтения и печати
              </span>
            </span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <p className="px-2 py-1.5 text-2xs leading-relaxed text-muted-foreground">
          Паролей и ключей в выгрузке нет — их нет и в самой системе. Содержимое вложений
          скачивается отдельно по ссылкам из файла. Каждая выгрузка попадает в журнал проекта.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
